import { useState } from "react";
import { Link } from "react-router-dom";
import { Eyebrow } from "@/lib/anim";

const BG = {
  bone: "#F8F5F0", paper: "#FFFDFA", sand: "#EDE6DA", stone: "#D9D0C1",
  charcoal: "#232220", charcoalDeep: "#151412", clay: "#9C6B54", clayDeep: "#7A5140", olive: "#4A5245",
};

export function Container({ children, className = "", size = "lg" }) {
  const max = size === "sm" ? "max-w-3xl" : size === "md" ? "max-w-5xl" : "max-w-[1200px]";
  return <div className={`${max} mx-auto px-6 md:px-10 ${className}`}>{children}</div>;
}

export function Section({ children, bg = "bone", dark = false, pad = true, className = "", id, contained = true }) {
  return (
    <section
      id={id}
      className={`${pad ? "py-20 md:py-28" : ""} ${dark ? "on-dark" : ""} ${className}`}
      style={{ backgroundColor: BG[bg] || bg, color: dark ? "var(--bone)" : undefined }}
    >
      {contained ? <Container>{children}</Container> : children}
    </section>
  );
}

export function Btn({ to, href, onClick, variant = "clay", className = "", children, type = "button", ...rest }) {
  const cls = `btn btn-${variant} ${className}`;
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button type={type} onClick={onClick} className={cls} {...rest}>{children}</button>;
}

export function ArrowLink({ to, href, children, dark = false, className = "", ...rest }) {
  const cls = `lnk ${dark ? "lnk-bone" : ""} ${className}`;
  const inner = <>{children}<span aria-hidden className="arrow-anim">→</span></>;
  if (to) return <Link to={to} className={cls} {...rest}>{inner}</Link>;
  return <a href={href} className={cls} {...rest}>{inner}</a>;
}

export function Crumbs({ items = [] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-8" data-testid="breadcrumb">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[12px] uppercase tracking-wide text-[var(--muted)]">
        {items.map((it, i) => (
          <li key={it.label} className="flex items-center gap-2">
            {it.to ? (
              <Link to={it.to} className="hover:text-[var(--clay-deep)] transition-colors">{it.label}</Link>
            ) : (
              <span className="text-[var(--ink)]">{it.label}</span>
            )}
            {i < items.length - 1 && <span aria-hidden className="text-[var(--stone)]">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageHero({ eyebrow, title, lead, crumbs, actions, children }) {
  return (
    <section className="pt-16 md:pt-24 pb-14 md:pb-16 border-b border-[var(--stone)]" style={{ backgroundColor: BG.bone }}>
      <Container>
        {crumbs && <Crumbs items={crumbs} />}
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="t-display2 max-w-4xl">{title}</h1>
        {lead && <p className="t-lead mt-6 measure text-[var(--muted)]">{lead}</p>}
        {actions && <div className="mt-9 flex flex-wrap gap-4">{actions}</div>}
        {children}
      </Container>
    </section>
  );
}

export function SectionHead({ eyebrow, title, lead, dark = false, className = "" }) {
  return (
    <div className={`max-w-2xl ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {title && <h2 className="t-h2">{title}</h2>}
      {lead && <p className={`t-lead mt-5 ${dark ? "text-[var(--stone)]" : "text-[var(--muted)]"}`}>{lead}</p>}
    </div>
  );
}

export function Stat({ n, c, dark = false, testid }) {
  return (
    <div data-testid={testid}>
      <div className="t-stat" style={{ color: dark ? "var(--bone)" : "var(--ink)" }}>{n}</div>
      <p className={`t-sm mt-3 ${dark ? "text-[var(--muted-dark)]" : "text-[var(--muted)]"}`}>{c}</p>
    </div>
  );
}

export function Accordion({ items = [], testid = "accordion" }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-[var(--stone)]" data-testid={testid}>
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={it.q} className="border-b border-[var(--stone)]">
            <button
              className="w-full flex items-center justify-between gap-6 py-6 text-left"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
              data-testid={`${testid}-trigger-${i}`}
            >
              <span className="t-h4 pr-4">{it.q}</span>
              <span className="acc-sign shrink-0" aria-hidden>{isOpen ? "\u2013" : "+"}</span>
            </button>
            <div className="acc-panel" style={{ maxHeight: isOpen ? "640px" : "0px" }}>
              <p className="pb-7 text-[var(--muted)] measure">{it.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function CTASection({
  eyebrow = "Start here",
  title,
  lead,
  primary,
  secondary,
  bg = "charcoal",
}) {
  const dark = ["charcoal", "charcoalDeep", "olive", "clay", "clayDeep"].includes(bg);
  return (
    <Section bg={bg} dark={dark} className="py-20 md:py-28">
      <div className="max-w-3xl">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="t-display2">{title}</h2>
        {lead && <p className="t-lead mt-6 measure text-[var(--stone)]">{lead}</p>}
        <div className="mt-10 flex flex-wrap gap-4">
          {primary}
          {secondary}
        </div>
      </div>
    </Section>
  );
}
